import {
  createMercadoPagoDepositReservation,
  notifyPaymentFailure,
} from '@features/booking/api/reservations';
import {
  createWlDepositMercadoPago,
} from '@features/booking/api/waitingList';
import type { DepositGrabMercadoPagoPayload } from '@features/booking/api/reservations';
import { forceLoadStripeV3 } from '@features/booking/hooks/useStripeV3';
import { handle3dsChallenge } from '@features/booking/hooks/handle3dsChallenge';
import { env } from '@core/config/env';
import { generateRandomDocumentId } from '@shared/utils/randomDocument';
import type { BaseReservationFields, CommitContext, CommitDerived, MpFlowFlagBox } from './shared';
import {
  persistSuccessAndAdvance,
  buildDepositFields,
  assertReservationId,
  MissingReservationIdError,
  TIP_STALE_CODES,
} from './shared';

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const obj = err as Record<string, unknown>;
    return (obj.errorMessage as string) || (obj.message as string) || 'Unknown error';
  }
  return 'Unknown error';
}

/**
 * MercadoPago deposit/no-show reservation path — with lazy Stripe v3 load for
 * 3DS.
 *
 * Hard invariants (see plan §3DS-SNAPSHOT §1 + §payment-branches §2):
 * - forceLoadStripeV3 is gated by `was3ds`. It MUST NOT run on happy-path
 *   responses (would needlessly kill the v2 SDK used by the MP form for
 *   future bookings on the same tab).
 * - Use env.stripeFallbackKey directly — BE creates the MP-fallback intent
 *   on platform credentials, not the restaurant's Stripe account.
 *   getStripePublishableKeyForTokenAccount must NOT be used here.
 * - was3ds gate skips the failure notify after a 3DS attempt: the reservation
 *   is already in pending_payment and handle3dsChallenge fired the
 *   non-blocking cancel-3ds-payment. (There is no client-side card-decline
 *   fallback — the backend now creates the reservation for declined no_show
 *   cards on the normal deposit-grab call.)
 * - threeDsResult.reservationId is the post-confirmation ID; use it for
 *   state writes, NOT response.reservationId.
 * - identityDocument is MP-mandatory: use real document if provided,
 *   otherwise generate a random one.
 *
 * The `flagBox.mpFlow3dsLoaded` field is written as soon as v3 loads. It
 * survives a throw because the caller owns the box — see MpFlowFlagBox doc.
 * Without this contract the outer catch couldn't gate setRequireReload and
 * a retry would silently produce a bad v2 token.
 */
export async function commitDepositMercadoPago(
  ctx: CommitContext,
  derived: CommitDerived,
  basePayload: BaseReservationFields,
  flagBox: MpFlowFlagBox,
): Promise<void> {
  const { booking, restaurant } = ctx;
  const { isWaitingList, effectiveGuaranteeType } = derived;

  const guaranteeTypeNarrow = effectiveGuaranteeType as 'deposit' | 'no_show';

  // MP SDK requires a numeric identityDocument field.
  // Use the user's real document if provided (billing flow), otherwise generate a random one.
  const mpIdentityDocument = booking.identityDocument
    ? Number(booking.identityDocument) || generateRandomDocumentId()
    : generateRandomDocumentId();

  const mpPayload: DepositGrabMercadoPagoPayload = {
    ...basePayload,
    date: basePayload.date as string,
    menu: basePayload.menu as number,
    ...buildDepositFields(ctx),
    type: guaranteeTypeNarrow,
    cardToken: booking.cardToken!,
    cardTokenStripe: booking.cardTokenStripe || '',
    paymentMethod: booking.paymentMethod || 'unknown',
    temp1: booking.encryptedCardNumber || '',
    temp3: booking.encryptedExpiry || '',
    temp4: booking.encryptedCvc || '',
    temp6: booking.encryptedHolderName || '',
    identityDocument: mpIdentityDocument,
  };

  let was3ds = false;
  try {
    const response = isWaitingList
      ? await createWlDepositMercadoPago(
          ctx.currentLanguage,
          { ...mpPayload, date: booking.wlDates },
        )
      : await createMercadoPagoDepositReservation(ctx.currentLanguage, mpPayload);

    was3ds = !!response.requires_action;
    // Write into the caller's box BEFORE any await that could throw.
    // If handle3dsChallenge rejects, the outer commitReservation catch
    // still reads the post-mutation value.
    flagBox.mpFlow3dsLoaded = was3ds;

    // Gate the v3 load behind requires_action. forceLoadStripeV3
    // unloads the v2 SDK (set by the MP form for legacy tokenization)
    // and loads v3 fresh — required because plain loadStripe() would
    // detect the v2 global and return v2, breaking handleNextAction.
    // See forceLoadStripeV3 docstring for the full rationale.
    //
    // Pubkey rationale: BE creates the MP-fallback Stripe intent on the
    // platform (new-account) credentials, not the restaurant's Stripe
    // account. Use env.stripeFallbackKey directly — same key the MP
    // form's v2 hook already uses. Do NOT route via
    // getStripePublishableKeyForTokenAccount: for MP restaurants that
    // resolves to restaurant.publicKeyPayment, which is a MercadoPago
    // key — not a Stripe key — and would crash handleNextAction.
    // T30 overlay: only fire on3dsStart/on3dsEnd when 3DS will actually fire,
    // which the existing ternary already enforces. Wrap the await so the
    // overlay shows during forceLoadStripeV3 + the challenge.
    let threeDsResult = null;
    if (was3ds) {
      ctx.on3dsStart?.();
      try {
        threeDsResult = await handle3dsChallenge(
          response,
          forceLoadStripeV3(env.stripeFallbackKey),
        );
      } finally {
        ctx.on3dsEnd?.();
      }
    }
    if (threeDsResult) {
      const reservationId = assertReservationId(threeDsResult.reservationId, {
        endpoint: 'confirm-3ds-payment',
        isWaitingList,
        flowType: booking.flowType,
        guaranteeType: guaranteeTypeNarrow,
        restaurant: restaurant.id,
      });
      persistSuccessAndAdvance(
        ctx,
        derived,
        reservationId,
        response.uniqueId,
        guaranteeTypeNarrow,
      );
      return;
    }

    const reservationId = assertReservationId(response.reservationId, {
      endpoint: isWaitingList ? 'deposit-waiting-list-mercadopago' : 'deposit-grab-mercadopago',
      isWaitingList,
      flowType: booking.flowType,
      guaranteeType: guaranteeTypeNarrow,
      restaurant: restaurant.id,
    });
    persistSuccessAndAdvance(
      ctx,
      derived,
      reservationId,
      response.uniqueId,
      guaranteeTypeNarrow,
    );
  } catch (mpErr) {
    // A 200-without-reservationId is a backend contract violation, not a payment
    // failure — re-throw to the outer commit catch without firing the payment-
    // failure notify below (which is for genuine declines/errors).
    if (mpErr instanceof MissingReservationIdError) {
      throw mpErr;
    }
    // The backend now creates the reservation when a no_show card is declined,
    // so there's no client-side card-decline fallback. Notify the failure and
    // re-throw to the outer catch where the UI maps the error.
    //
    // 3DS already mutated state (reservation in pending_payment;
    // handle3dsChallenge fired non-blocking cancel-3ds-payment on failure), so
    // skip the notify in that case. flagBox.mpFlow3dsLoaded is already true at
    // this point (set above the throw), so the outer catch will correctly fire
    // setRequireReload.
    //
    // Also skip for TIP_STALE_CODES: GrabController returns these before
    // ProcessPayment even runs (see shared.ts's TIP_STALE_CODES doc), so no
    // card was charged, declined, or touched at all. notifyPaymentFailure
    // writes a CreditCardErrorsLog row and emails staff "Pago fallido en
    // reserva" — both false alarms for what this unit treats as an expected,
    // benign recovery path (the guest is routed back to TIME, not told their
    // card failed).
    if (!was3ds && !TIP_STALE_CODES.has(getErrorMessage(mpErr))) {
      await notifyPaymentFailure({
        paymentMethod: booking.paymentMethod || undefined,
        errorMsg: getErrorMessage(mpErr),
        paymentProcessor: 'mercadopago',
        restaurantId: restaurant.id,
        billingInfoEntered: 0,
        firstTry: 0,
      }).catch(() => { /* non-blocking */ });
    }
    throw mpErr;
  }
}
